import { PermissionGate } from "@/components/common/PermissionGate"
import { toast } from "@/components/common/Toast/Toast"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { PERMISSIONS } from "@/constants/permissions"
import { isPermissionDenied } from "@/hooks/useGuardedMutation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { RotateCcw } from "lucide-react"
import { useResetStatement } from "../../../../../api/declarationsApi"

export interface ResetStatementButtonProps {
  declId: string
  statementId: string
}

/**
 * The card header's "reset to the AI proposal" icon. A child component of its
 * own, mounted by `UnitCard` only while `MC_STATEMENT_RESET` is on AND the
 * reviewer touched the statement, so `useResetStatement()` (a mutation plus
 * its permission guard) does not exist at all on a flag-off card.
 *
 * Hidden without the review permission, like every other review write in
 * this mask. The error toast skips a permission refusal as a second line of
 * defence (`isPermissionDenied`): the gate already hides the button, so a
 * refusal here means the permission changed under an open mask, and "Reset
 * failed" would misname it. `useResetStatement` never toasts itself.
 */
export function ResetStatementButton({
  declId,
  statementId,
}: ResetStatementButtonProps) {
  const { translate } = useTranslation()
  const reset = useResetStatement()

  const handleReset = () => {
    reset.mutate(
      { declarationId: declId, statementId },
      {
        onSuccess: () => toast.success(translate("MC_DECL_V2_UNIT_RESET_DONE")),
        onError: (error) => {
          if (isPermissionDenied(error)) return
          toast.error(translate("MC_DECL_V2_UNIT_RESET_FAILED"))
        },
      },
    )
  }

  return (
    <PermissionGate permission={PERMISSIONS.MC_DECLARATIONS_REVIEW}>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="sv2-iconbtn"
              aria-label={translate("MC_DECL_V2_UNIT_RESET")}
              data-testid="sv2-reset"
              disabled={reset.isPending}
              onClick={handleReset}
            >
              <RotateCcw size={14} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">
            {translate("MC_DECL_V2_UNIT_RESET")}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </PermissionGate>
  )
}
